// Device compass heading (magnetometer). Shows which way the user is physically
// facing — even when stationary, unlike GPS `heading` (course over ground) which
// is only valid while moving. iOS 13+ requires an explicit permission request
// from a user gesture, so call enable() from a tap (e.g. the My-Location button).

type Listener = (heading: number | null) => void;

let current: number | null = null;
const listeners = new Set<Listener>();
let started = false;

const norm = (h: number) => ((h % 360) + 360) % 360;

function applyHeading(h: number | null) {
  if (h === null || Number.isNaN(h)) return;
  current = norm(h);
  listeners.forEach((l) => l(current));
}

// Derive a compass heading from an orientation event. `trustAlpha` says whether a
// bare `alpha` on THIS event is a true-north (absolute) reading. iOS always
// provides webkitCompassHeading (already absolute), so it's used regardless. On
// Android only the `deviceorientationabsolute` event — or a `deviceorientation`
// event whose own `.absolute` flag is true — is north-referenced. A *relative*
// `deviceorientation` alpha points at an arbitrary reference set when listening
// began, so using it would rotate the facing cone the wrong way (and fight the
// absolute readings, which also fire). In that case return null and wait for an
// absolute reading instead of showing a wrong heading.
function readHeading(
  e: DeviceOrientationEvent & { webkitCompassHeading?: number },
  trustAlpha: boolean
): number | null {
  if (typeof e.webkitCompassHeading === 'number') {
    return e.webkitCompassHeading; // iOS: already a compass heading (0 = N, clockwise)
  }
  if (typeof e.alpha === 'number' && trustAlpha) {
    return norm(360 - e.alpha);
  }
  return null;
}

function onAbsolute(e: Event) {
  applyHeading(readHeading(e as DeviceOrientationEvent, true));
}

function onRelative(e: Event) {
  const de = e as DeviceOrientationEvent;
  applyHeading(readHeading(de, de.absolute === true));
}

function startListening() {
  if (started || typeof window === 'undefined') return;
  // The absolute (true-north referenced) event is always trustworthy; the plain
  // event only when it flags itself absolute.
  window.addEventListener('deviceorientationabsolute', onAbsolute, true);
  window.addEventListener('deviceorientation', onRelative, true);
  started = true;
}

export const deviceHeading = {
  get current() {
    return current;
  },
  supported() {
    return typeof window !== 'undefined' && 'DeviceOrientationEvent' in window;
  },
  /** Request permission (iOS) and start listening. Must be called from a gesture. */
  async enable(): Promise<'granted' | 'denied' | 'unsupported'> {
    if (!this.supported()) return 'unsupported';
    const DOE = (window as unknown as { DeviceOrientationEvent?: { requestPermission?: () => Promise<string> } })
      .DeviceOrientationEvent;
    if (typeof DOE?.requestPermission === 'function') {
      try {
        if ((await DOE.requestPermission()) !== 'granted') return 'denied';
      } catch {
        return 'denied';
      }
    }
    startListening();
    return 'granted';
  },
  subscribe(l: Listener): () => void {
    listeners.add(l);
    if (current !== null) l(current);
    return () => {
      listeners.delete(l);
    };
  },
};
